/* React의 KeyboardEvent를 그대로 들여오면 아래 focus trap이 쓰는 **DOM**
   KeyboardEvent를 가려 window.addEventListener 시그니처가 어긋납니다. */
import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import type {
  ResourceBuiltinLink,
  ResourceDetailResponse,
} from "@k8s-dashboard/contracts";
import { useReturnFocus } from "@/app/focus";
import { readDrawerPreference, writeDrawerPreference, type PreferenceScope } from "@/app/preference-storage";
import { useT } from "@/i18n/I18nProvider";
import { ChangeReviewPanel } from "./ChangeReviewPanel";
import { DeletePanel } from "./DeletePanel";
import { identityKey } from "./dryrun";
import { ResourceDetailSections } from "./ResourceDetailSections";
import {
  builtinRelationFocusKey,
  type BuiltinRelationsView,
} from "./builtin-relations";
import { requestErrorMessage } from "./state";

/**
 * 선택한 항목의 상세 (ADR 0018 결정 5·6, ADR 0019 Phase 1)
 * --------------------------------------------------------------------------
 * 기본은 **읽기 전용**입니다. 서버가 정제해 내려준 YAML을 그대로 보여주기만 하고,
 * Secret의 data/stringData는 애초에 응답에 없습니다. 무엇이 제거됐는지는 숨기지
 * 않고 표시합니다 — 가려졌다는 사실 자체가 정보입니다.
 *
 * 서버가 이 GVR에 검토 capability를 준 경우에만 "변경 검토" 서브탭이 생깁니다.
 * capability는 **서버가 준 descriptor.dryRun 하나**에서만 옵니다 — kind·verbs·
 * 사용자 역할로 추론하지 않습니다. 탭은 새 라우트가 아니라 이 드로어 안입니다.
 */

type DrawerTab = "view" | "review" | "delete";
const TAB_LABEL: Record<DrawerTab, string> = {
  view: "매니페스트",
  review: "변경 검토",
  delete: "삭제",
};
/* id는 고정 문자열입니다 — 이름·UID 같은 신원을 속성에 싣지 않습니다. */
const TAB_ID: Record<DrawerTab, string> = {
  view: "resource-drawer-tab-view",
  review: "resource-drawer-tab-review",
  delete: "resource-drawer-tab-delete",
};
const PANEL_ID: Record<DrawerTab, string> = {
  view: "resource-drawer-panel-view",
  review: "resource-drawer-panel-review",
  delete: "resource-drawer-panel-delete",
};

export const DRAWER_DEFAULT_WIDTH = 880;
export const DRAWER_MIN_WIDTH = 560;
export const DRAWER_MAX_WIDTH = 1200;
export const DRAWER_RESIZE_STEP = 16;
export const DRAWER_VIEWPORT_ALLOWANCE = 32;
export const DRAWER_MOBILE_BREAKPOINT = 900;

export function ResourceDetailDrawer({
  open,
  loading,
  detail,
  error,
  onClose,
  canReview = false,
  canApply = false,
  canDelete = false,
  onChanged,
  relations,
  onResourceLink,
  preferenceScope,
  favorite,
  suspended = false,
}: {
  open: boolean;
  loading: boolean;
  detail: ResourceDetailResponse | undefined;
  error: unknown;
  onClose: () => void;
  /** 서버가 준 descriptor.dryRun. 기본은 false이며 화면이 추론하지 않습니다. */
  canReview?: boolean;
  /** 서버가 준 descriptor.apply/delete. 셋은 각각 별도의 배포 opt-in입니다. */
  canApply?: boolean;
  canDelete?: boolean;
  /** 적용·삭제가 끝나면 상위가 목록·상세를 다시 조회합니다. */
  onChanged?: () => void;
  /** 상위가 현재 detail focus에 맞춰 선별한 표시 전용 관계 모델입니다. */
  relations?: BuiltinRelationsView;
  onResourceLink?: (link: ResourceBuiltinLink) => void;
  preferenceScope?: PreferenceScope;
  favorite?: { active: boolean; disabled: boolean; onToggle: () => void };
  /** 현재 인증 generation의 Scope가 아직 검증되지 않은 상태입니다. */
  suspended?: boolean;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const [tab, setTab] = useState<DrawerTab>("view");
  const { t } = useT("resources");
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const [desktopWidth, setDesktopWidth] = useState(DRAWER_DEFAULT_WIDTH);
  const suspendedRef = useRef(suspended);
  suspendedRef.current = suspended;
  const tabRefs = useRef<Record<DrawerTab, HTMLButtonElement | null>>({
    view: null,
    review: null,
    delete: null,
  });

  /* 탭 구성은 **서버 capability에서만** 옵니다. 검토·삭제는 서로 다른 opt-in이라
     하나만 켜진 배포가 정상입니다. 순서는 파괴력이 커지는 방향입니다. */
  const tabs: DrawerTab[] = ["view"];
  if (canReview) tabs.push("review");
  if (canDelete) tabs.push("delete");

  /* 대상 신원은 **전부** 봅니다. uid+resourceVersion만 보면 클러스터·GVR·namespace가
     바뀌었는데 우연히 같은 uid를 만난 경우를 놓칩니다. 검토 패널과 같은 키를 씁니다. */
  const identity = detail ? identityKey(detail) : "";
  const narrow = viewportWidth <= DRAWER_MOBILE_BREAKPOINT;
  const desktopMax = Math.min(DRAWER_MAX_WIDTH, viewportWidth - DRAWER_VIEWPORT_ALLOWANCE);
  const clampDesktop = (value: number) => Math.min(desktopMax, Math.max(DRAWER_MIN_WIDTH, Math.round(value)));
  const renderedWidth = narrow ? Math.max(0, viewportWidth - DRAWER_VIEWPORT_ALLOWANCE) : clampDesktop(desktopWidth);

  useEffect(() => {
    const resize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  useEffect(() => {
    if (!preferenceScope) {
      setDesktopWidth(DRAWER_DEFAULT_WIDTH);
      return;
    }
    setDesktopWidth(readDrawerPreference(preferenceScope) ?? DRAWER_DEFAULT_WIDTH);
  }, [preferenceScope]);

  const persistWidth = (width: number) => {
    setDesktopWidth(width);
    if (!suspendedRef.current && preferenceScope) writeDrawerPreference(preferenceScope, width);
  };

  const startResize = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (suspendedRef.current || !Number.isFinite(event.clientX)) return;
    const startX = event.clientX;
    const startWidth = renderedWidth;
    let latest = startWidth;
    const move = (next: PointerEvent) => {
      if (!Number.isFinite(next.clientX)) return;
      latest = clampDesktop(startWidth + startX - next.clientX);
      setDesktopWidth(latest);
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      persistWidth(latest);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up, { once: true });
  };

  const resizeKey = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (suspendedRef.current) return;
    let next: number | undefined;
    if (event.key === "ArrowLeft") next = desktopWidth + DRAWER_RESIZE_STEP;
    else if (event.key === "ArrowRight") next = desktopWidth - DRAWER_RESIZE_STEP;
    else if (event.key === "Home") next = DRAWER_MIN_WIDTH;
    else if (event.key === "End") next = desktopMax;
    if (next === undefined) return;
    event.preventDefault();
    persistWidth(clampDesktop(next));
  };

  /* 기본은 언제나 View입니다. 다시 열거나, 대상이 바뀌거나, capability가 사라지면
     되돌아갑니다 — 그때 검토 패널이 언마운트되면서 편집 초안과 진행 중 요청도
     함께 사라집니다. */
  useEffect(() => {
    setTab("view");
  }, [open, canReview, canDelete, identity]);

  /* 닫히면 **연 곳으로** 포커스를 돌려놓습니다. 규칙은 공용 훅이 소유합니다 —
     모달마다 복붙하면 한 곳만 고쳐지고 나머지는 포커스를 body에 흘립니다. (ADR 0025) */
  useReturnFocus(open);

  useEffect(() => {
    if (!open || suspended) return;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (e.defaultPrevented || e.isComposing) return;
        onClose();
        return;
      }
      /* 열려 있는 동안 포커스는 대화상자 안에 머뭅니다. */
      if (e.key !== "Tab" || !dialogRef.current) return;
      const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
        'button, [href], textarea, input, select, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, suspended]);

  /* 화살표·Home·End는 포커스와 선택을 **함께** 옮깁니다(WAI-ARIA tabs).
     객체 조회 대신 switch를 씁니다 — `moves["constructor"]` 같은 프로토타입 키가
     함수를 돌려주면 undefined 검사를 지나 인덱스로 쓰이게 됩니다. */
  const onTabKeyDown = (
    event: ReactKeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    let next: number | undefined;
    switch (event.key) {
      case "ArrowRight":
        next = (index + 1) % tabs.length;
        break;
      case "ArrowLeft":
        next = (index - 1 + tabs.length) % tabs.length;
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = tabs.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    /* 화살표 이동이 드로어 밖으로 새지 않게 여기서 멈춥니다. */
    event.stopPropagation();
    const target = tabs[next];
    setTab(target);
    tabRefs.current[target]?.focus();
  };

  if (!open) return null;
  const message = requestErrorMessage(error);

  return (
    <div
      className="resource-drawer__overlay"
      role="presentation"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        className="resource-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="resource-drawer-title"
        style={{ width: renderedWidth }}
        onClick={(e) => e.stopPropagation()}
      >
        {!narrow && (
          <div
            className="resource-drawer__resizer"
            role="separator"
            aria-orientation="vertical"
            aria-label={t("resources.drawer.resize")}
            aria-valuemin={DRAWER_MIN_WIDTH}
            aria-valuemax={desktopMax}
            aria-valuenow={renderedWidth}
            tabIndex={0}
            onPointerDown={startResize}
            onKeyDown={resizeKey}
          />
        )}
        <div className="resource-drawer__header">
          <div>
            <h2 id="resource-drawer-title" className="resource-drawer__title">
              {detail ? `${detail.kind} · ${detail.name}` : "매니페스트"}
            </h2>
            {detail && (
              <p className="resource-drawer__meta muted">
                {detail.namespace ? `${detail.namespace} · ` : ""}
                {detail.apiVersion}
              </p>
            )}
          </div>
          <div className="resource-drawer__actions">
            {detail && favorite && (
              <button
                type="button"
                className="linkish"
                aria-pressed={favorite.active}
                disabled={favorite.disabled || suspended}
                onClick={favorite.onToggle}
              >
                {favorite.active ? t("resources.favorite.remove") : t("resources.favorite.add")}
              </button>
            )}
            <button ref={closeRef} type="button" className="linkish" onClick={onClose}>닫기</button>
          </div>
        </div>

        {loading ? (
          <div className="state" aria-busy="true">
            <span className="state__title">매니페스트를 불러오는 중…</span>
          </div>
        ) : message || !detail ? (
          <div className="state state--error" role="alert">
            <span className="state__glyph" aria-hidden="true">
              ✕
            </span>
            <span className="state__title">
              매니페스트를 불러오지 못했습니다
            </span>
            <span className="state__detail">
              {message ?? "잠시 후 다시 시도하세요."}
            </span>
          </div>
        ) : tabs.length === 1 ? (
          /* capability가 없으면 오늘의 화면 그대로입니다 — 탭도 편집기도 없습니다. */
          <ReadOnlyManifest
            detail={detail}
            relations={relations}
            onResourceLink={onResourceLink}
          />
        ) : (
          <>
            <div
              className="chips resource-drawer__tabs"
              role="tablist"
              aria-label="상세 보기 방식"
            >
              {tabs.map((id, index) => (
                <button
                  key={id}
                  ref={(el) => {
                    tabRefs.current[id] = el;
                  }}
                  type="button"
                  role="tab"
                  id={TAB_ID[id]}
                  className="chip"
                  aria-selected={tab === id}
                  aria-controls={PANEL_ID[id]}
                  tabIndex={tab === id ? 0 : -1}
                  disabled={suspended}
                  onClick={() => setTab(id)}
                  onKeyDown={(e) => onTabKeyDown(e, index)}
                >
                  {TAB_LABEL[id]}
                </button>
              ))}
            </div>

            {/* 활성 탭만 마운트합니다. 숨겨 두면 편집기가 DOM에 남아 초안과 진행 중
                요청이 살아 있고, focus trap이 보이지 않는 요소를 잡습니다. */}
            {tab === "view" && (
              <div
                id={PANEL_ID.view}
                role="tabpanel"
                aria-labelledby={TAB_ID.view}
                tabIndex={-1}
              >
                <ReadOnlyManifest
                  detail={detail}
                  relations={relations}
                  onResourceLink={onResourceLink}
                />
              </div>
            )}
            {tab === "review" && (
              <div
                id={PANEL_ID.review}
                role="tabpanel"
                aria-labelledby={TAB_ID.review}
                tabIndex={-1}
              >
                {/* key가 신원입니다. 없으면 대상이 바뀐 렌더에서 **같은 인스턴스**가
                    새 detail을 받고, 리셋 effect가 돌기 전까지 이전 초안과 진행 중
                    요청이 새 대상에 붙어 있게 됩니다. key를 두면 그 창 자체가
                    사라집니다 — 옛 인스턴스가 먼저 언마운트되고 새 인스턴스가 섭니다.
                    적용 단계는 canApply일 때만 붙습니다 — 서버가 grant를 주더라도
                    화면 capability와 어긋나면 진입점을 만들지 않습니다. */}
                <ChangeReviewPanel
                  key={identity}
                  detail={detail}
                  suspended={suspended}
                  onApplied={canApply && onChanged ? () => {
                    if (!suspendedRef.current) onChanged();
                  } : undefined}
                />
              </div>
            )}
            {tab === "delete" && !suspended && (
              <div
                id={PANEL_ID.delete}
                role="tabpanel"
                aria-labelledby={TAB_ID.delete}
                tabIndex={-1}
              >
                <DeletePanel
                  key={identity}
                  detail={detail}
                  onDeleted={(result) => {
                    if (suspendedRef.current) return;
                    onChanged?.();
                    /* **객체가 실제로 사라졌을 때만** 닫습니다. finalizing·unconfirmed는
                       사용자가 읽어야 하는 사실이고, 여기서 닫으면 그 문장이 화면에
                       0.1초 떠 있다 사라집니다 — 삭제됐다고 오해하게 됩니다. */
                    if (result.outcome === "deleted") onClose();
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * 정제된 읽기 전용 매니페스트입니다.
 *
 * 탭이 있든 없든 **같은 마크업**을 씁니다 — 두 벌로 나누면 한쪽만 고쳐져 capability
 * 유무에 따라 다른 정보를 보여 주게 됩니다.
 */
function ReadOnlyManifest({
  detail,
  relations,
  onResourceLink,
}: {
  detail: ResourceDetailResponse;
  relations?: BuiltinRelationsView;
  onResourceLink?: (link: ResourceBuiltinLink) => void;
}) {
  return (
    <>
      {/* 구조화 절(ADR 0065)은 **YAML보다 먼저** 옵니다 — 운영자가 먼저 보는 것은
          "지금 어떤 상태인가"이고, 매니페스트 원문은 그 뒤에 확인하는 근거입니다. */}
      <ResourceDetailSections
        detail={detail}
        relations={
          relations?.focusKey === builtinRelationFocusKey(detail)
            ? relations
            : undefined
        }
        onResourceLink={onResourceLink}
      />
      <dl className="resource-drawer__facts">
        <div>
          <dt>UID</dt>
          <dd className="ds-ident">{detail.uid}</dd>
        </div>
        <div>
          <dt>resourceVersion</dt>
          <dd className="ds-ident">{detail.resourceVersion || "-"}</dd>
        </div>
      </dl>

      <p className="resource-drawer__redaction" role="note">
        <span className="resource-drawer__redaction-mark" aria-hidden="true">
          !
        </span>
        서버가 정제한 읽기 전용 매니페스트입니다. Secret의 <code>data</code>/
        <code>stringData</code>와 서버 관리 필드·민감 annotation은{" "}
        <strong>서버에서 제거된 뒤</strong> 전달됩니다. 원문 조회 경로는
        없습니다.
        {detail.redacted && detail.redacted.length > 0 && (
          <>
            {" "}
            제거된 항목:{" "}
            <span className="ds-ident">{detail.redacted.join(", ")}</span>
          </>
        )}
      </p>

      <pre
        className="resource-drawer__yaml"
        tabIndex={0}
        aria-label={`${detail.name} 매니페스트 (YAML, 읽기 전용)`}
      >
        {detail.yaml}
      </pre>
    </>
  );
}
